
    import { config } from '@xiboplayer/utils';
    import { RestClient, XmdsClient } from '@xiboplayer/xmds';

    // Inject version from Vite build
    const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?';
    document.getElementById('version-footer').textContent = `Xibo Player v${appVersion}`;

    // ── Elements ──
    const form = document.getElementById('setup-form');
    const errorEl = document.getElementById('error');
    const submitBtn = document.getElementById('submit-btn');
    const phaseUnlock = document.getElementById('phase-unlock');
    const phaseSetup = document.getElementById('phase-setup');
    const phaseWaiting = document.getElementById('phase-waiting');
    const countdownEl = document.getElementById('countdown');
    const waitingNameEl = document.getElementById('waiting-display-name');
    const btnBack = document.getElementById('btn-back');
    const successFlash = document.getElementById('success-flash');

    // Show hardware key
    const hwKey = config.hardwareKey;
    document.getElementById('hw-key-display').textContent = hwKey;
    document.getElementById('hw-key-waiting').textContent = hwKey;

    // ── State ──
    const previousCmsUrl = config.cmsUrl || '';
    let pollTimer = null;
    let countdownTimer = null;
    let pollSeconds = 15;

    // ── Transport: try REST first, fall back to SOAP ──
    let transport = null;
    async function getTransport() {
      if (transport) return transport;
      try {
        const rest = new RestClient(config);
        await rest.registerDisplay();
        console.log('[Setup] Using REST transport');
        transport = rest;
      } catch (e) {
        console.log('[Setup] REST unavailable, using XMDS/SOAP:', e.message);
        transport = new XmdsClient(config);
      }
      return transport;
    }

    // ── Helpers ──
    function showPhase(phase) {
      phaseUnlock.classList.remove('active');
      phaseSetup.classList.remove('active');
      phaseWaiting.classList.remove('active');
      phase.classList.add('active');
    }

    function showError(msg) {
      errorEl.textContent = msg;
      errorEl.style.display = 'block';
    }

    function hideError() {
      errorEl.style.display = 'none';
    }

    function startCountdown() {
      let remaining = pollSeconds;
      countdownEl.textContent = remaining;
      clearInterval(countdownTimer);
      countdownTimer = setInterval(() => {
        remaining--;
        countdownEl.textContent = Math.max(0, remaining);
        if (remaining <= 0) clearInterval(countdownTimer);
      }, 1000);
    }

    async function showSuccess() {
      // If CMS URL changed, wipe all cached content (media, layouts, widgets
      // from the old CMS are useless) and the SW's cached index.html (which
      // may have a stale config injection script).
      const newUrl = document.getElementById('cms-url').value.trim().replace(/\/$/, '');
      if (newUrl !== previousCmsUrl) {
        console.log('[Setup] CMS URL changed — wiping cached content');
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      successFlash.classList.add('active');
      setTimeout(() => {
        window.location.href = '/player/index.html' + window.location.search;
      }, 800);
    }

    // ── Authorization Polling ──
    async function checkAuthorization() {
      try {
        const client = await getTransport();
        const result = await client.registerDisplay();
        console.log('[Setup] Poll result:', result.code, result.message);

        if (result.code === 'READY') {
          // Authorized!
          clearInterval(pollTimer);
          clearInterval(countdownTimer);
          showSuccess();
          return;
        }

        // Still waiting — restart countdown
        startCountdown();
      } catch (error) {
        console.warn('[Setup] Poll failed:', error.message);
        startCountdown();
      }
    }

    function startPolling(displayName) {
      waitingNameEl.textContent = displayName;
      showPhase(phaseWaiting);
      startCountdown();
      pollTimer = setInterval(checkAuthorization, pollSeconds * 1000);
    }

    function stopPolling() {
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
      pollTimer = null;
    }

    // ── Auto-authorize via CMS API (through /api/* forward proxy) ──
    async function tryAutoAuthorize(cmsUrl, hardwareKey) {
      const clientId = document.getElementById('api-client-id').value.trim();
      const clientSecret = document.getElementById('api-client-secret').value.trim();
      if (!clientId || !clientSecret) return false;

      try {
        // Step 1: OAuth2 token via proxy
        const tokenResp = await fetch('/api/authorize/access_token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ grant_type: 'client_credentials', client_id: clientId, client_secret: clientSecret }),
        });
        if (!tokenResp.ok) throw new Error(`OAuth2 failed: ${tokenResp.status}`);
        const { access_token } = await tokenResp.json();

        // Step 2: Find display by hardwareKey via proxy
        const displayResp = await fetch(
          `/api/display?hardwareKey=${encodeURIComponent(hardwareKey)}`,
          { headers: { Authorization: `Bearer ${access_token}` } }
        );
        if (!displayResp.ok) throw new Error(`Find display failed: ${displayResp.status}`);
        const displays = await displayResp.json();
        if (!displays.length) { console.log('[Setup] Display not found via API (may not be registered yet)'); return false; }

        const display = displays[0];
        if (display.licensed === 1) { console.log('[Setup] Display already authorized'); return true; }

        // Step 3: Authorize display via proxy
        const authResp = await fetch(
          `/api/display/authorise/${display.displayId}`,
          { method: 'PUT', headers: { Authorization: `Bearer ${access_token}` } }
        );
        if (!authResp.ok) throw new Error(`Authorize failed: ${authResp.status}`);

        console.log('[Setup] Display auto-authorized via proxy!');
        config.apiClientId = clientId;
        config.apiClientSecret = clientSecret;
        return true;
      } catch (error) {
        console.warn('[Setup] Auto-authorize failed:', error.message);
        return false;
      }
    }

    // ── Unlock gate for reconfiguration ──
    const isReconfigure = config.isConfigured() && config.cmsKey;
    const alreadyUnlocked = new URLSearchParams(window.location.search).has('unlocked');
    const btnBackPlayer = document.getElementById('btn-back-player');
    if (isReconfigure && !alreadyUnlocked) {
      // Show unlock phase instead of setup form
      showPhase(phaseUnlock);
    }
    if (isReconfigure || alreadyUnlocked) {
      // Show "Back to Player" on the setup form
      btnBackPlayer.style.display = 'block';
      // Pre-fill form with existing config
      document.getElementById('cms-url').value = config.cmsUrl;
      document.getElementById('cms-key').value = config.cmsKey;
      document.getElementById('display-name').value = config.displayName;
      if (config.apiClientId) {
        document.getElementById('api-client-id').value = config.apiClientId;
        document.getElementById('api-client-secret').value = config.apiClientSecret || '';
      }
    }

    // ── Back to Player (cancel reconfiguration) ──
    function goBackToPlayer() {
      stopPolling();
      window.location.href = '/player/index.html' + window.location.search;
    }
    btnBackPlayer.addEventListener('click', goBackToPlayer);

    // Esc also goes back to player when reconfiguring
    if (isReconfigure || alreadyUnlocked) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          goBackToPlayer();
        }
      });
    }

    // ── Unlock form handler ──
    document.getElementById('unlock-form').addEventListener('submit', (e) => {
      e.preventDefault();
      const unlockError = document.getElementById('unlock-error');
      const enteredKey = document.getElementById('unlock-key').value.trim();

      if (enteredKey === config.cmsKey) {
        unlockError.style.display = 'none';
        // Pre-fill form with existing config
        document.getElementById('cms-url').value = config.cmsUrl;
        document.getElementById('cms-key').value = config.cmsKey;
        document.getElementById('display-name').value = config.displayName;
        if (config.apiClientId) {
          document.getElementById('api-client-id').value = config.apiClientId;
          document.getElementById('api-client-secret').value = config.apiClientSecret || '';
        }
        showPhase(phaseSetup);
      } else {
        unlockError.textContent = 'Incorrect CMS key';
        unlockError.style.display = 'block';
      }
    });

    // ── Pre-fill if first-time setup (no unlock gate) ──
    if (!isReconfigure && config.isConfigured()) {
      document.getElementById('cms-url').value = config.cmsUrl;
      document.getElementById('cms-key').value = config.cmsKey;
      document.getElementById('display-name').value = config.displayName;
      if (config.apiClientId) {
        document.getElementById('api-client-id').value = config.apiClientId;
        document.getElementById('api-client-secret').value = config.apiClientSecret || '';
      }
    }

    // ── Form Submit ──
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      hideError();

      const cmsUrl = document.getElementById('cms-url').value.trim().replace(/\/$/, '');
      const cmsKey = document.getElementById('cms-key').value.trim();
      const displayName = document.getElementById('display-name').value.trim();

      // Write config.json via proxy (master config file)
      // The boot path (proxy injection → Config.load()) handles the rest.
      try {
        submitBtn.textContent = 'Saving...';
        submitBtn.disabled = true;

        const saveResp = await fetch('/config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cmsUrl, cmsKey, displayName }),
        });
        if (!saveResp.ok) throw new Error('Failed to save config');

        // Also update in-memory config for the test connection below
        config.data.cmsUrl = cmsUrl;
        config.data.cmsKey = cmsKey;
        config.data.displayName = displayName;
        config.save();

        // Backup hardware key to IndexedDB for stability
        backupHardwareKey(config.hardwareKey);

        // Test connection
        submitBtn.textContent = 'Connecting...';

        // Try REST first, fall back to SOAP
        transport = null; // reset transport for fresh detection
        const client = await getTransport();
        const result = await client.registerDisplay();

        if (result.code === 'READY') {
          showSuccess();
        } else {
          // Try auto-authorize via CMS API if credentials provided
          submitBtn.textContent = 'Authorizing...';
          const autoAuthed = await tryAutoAuthorize(cmsUrl, hwKey);

          if (autoAuthed) {
            // Verify registration
            const recheck = await client.registerDisplay();
            if (recheck.code === 'READY') {
              showSuccess();
              return;
            }
          }

          // Not authorized yet — switch to polling phase
          startPolling(displayName);
        }
      } catch (error) {
        showError(`Connection failed: ${error.message}`);
        submitBtn.textContent = 'Connect';
        submitBtn.disabled = false;
      }
    });

    // ── Back button ──
    btnBack.addEventListener('click', () => {
      stopPolling();
      showPhase(phaseSetup);
      submitBtn.textContent = 'Connect';
      submitBtn.disabled = false;
    });

    // ── IndexedDB hardware key backup ──
    async function backupHardwareKey(key) {
      try {
        const req = indexedDB.open('xibo-hw-backup', 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains('keys')) {
            db.createObjectStore('keys');
          }
        };
        req.onsuccess = () => {
          const db = req.result;
          const tx = db.transaction('keys', 'readwrite');
          tx.objectStore('keys').put(key, 'hardwareKey');
          tx.oncomplete = () => db.close();
        };
      } catch (e) {
        console.warn('[Setup] IndexedDB backup failed:', e);
      }
    }

    // ── Enable submit ──
    submitBtn.disabled = false;
    submitBtn.textContent = 'Connect';

    // ── Auto-resume polling if already registered but not authorized ──
    // Skip auto-resume when reconfiguring (user explicitly opened setup)
    if (config.isConfigured() && !isReconfigure) {
      (async () => {
        try {
          const client = await getTransport();
          const result = await client.registerDisplay();
          if (result.code === 'READY') {
            window.location.href = '/player/index.html';
          } else {
            // Not authorized — go straight to polling
            startPolling(config.displayName);
          }
        } catch (e) {
          // Connection failed, show form
          console.log('[Setup] Auto-check failed, showing form');
        }
      })();
    }
  